import React, { useMemo, useState } from 'react';
import { Alert, Button, Col, Form, FormField, FormRow, Right, formWithValidation, Row } from '@edgebox/react-components';
import type { SetAiProviderAccessKeyInput } from '@edgebox/sync-core-rest-client';
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { IsOptional, IsString, MaxLength, MinLength } from 'class-validator';

class AiProviderAccessKeyDraft {
  @IsString()
  @MinLength(1)
  @MaxLength(200)
  name!: string;

  @IsString()
  @IsOptional()
  @MinLength(1)
  @MaxLength(200)
  accessKeyName?: string;

  @IsString()
  @MinLength(1)
  @MaxLength(10_000)
  accessKeyEncrypted!: string;
}

const AccessKeyForm = formWithValidation<AiProviderAccessKeyDraft, AiProviderAccessKeyDraft>(
  (values) => plainToInstance(AiProviderAccessKeyDraft, values),
  (entity) => instanceToPlain(entity) as AiProviderAccessKeyDraft
);

export function AiProviderServiceSecretEditor({
  initialValues,
  submitLabel,
  onSubmit,
}: {
  initialValues: SetAiProviderAccessKeyInput;
  submitLabel: string;
  onSubmit: (values: SetAiProviderAccessKeyInput) => Promise<void>;
}) {
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(undefined);

  const draft = useMemo(() => {
    return plainToInstance(AiProviderAccessKeyDraft, initialValues);
  }, [initialValues]);

  return (
    <AccessKeyForm
      initialValues={draft}
      onSubmit={async (values) => {
        setSaving(true);
        setSubmitError(undefined);
        try {
          await onSubmit({
            name: values.name,
            accessKeyName: values.accessKeyName?.length ? values.accessKeyName : undefined,
            accessKeyEncrypted: values.accessKeyEncrypted,
          });
        } catch (e) {
          setSubmitError(e);
        } finally {
          setSaving(false);
        }
      }}
    >
      {() => {
        return (
          <>
            {submitError ? (
              <Alert variant="danger" className="mb-3">
                Failed to save: {submitError instanceof Error ? submitError.message : String(submitError)}
              </Alert>
            ) : null}
            <Row>
              <Col md={6}>
                <FormRow name={'name'} label={'Name'} labelPlacement={'above'} required>
                  <FormField name={'name'} type={'text'} noRow />
                </FormRow>
              </Col>

              <Col md={6}>
                <FormRow name={'accessKeyName'} label={'Access key name'} labelPlacement={'above'}>
                  <FormField name={'accessKeyName'} type={'text'} noRow />
                </FormRow>
              </Col>
            </Row>

            <FormRow name={'accessKeyEncrypted'} label={'Access key'} labelPlacement={'above'} required>
              <FormField name={'accessKeyEncrypted'} rows={4} noRow />
              <Form.Text className="text-muted">Keys are write-only and cannot be read back.</Form.Text>
            </FormRow>

            <Right className="pe-1">
              <Button variant="primary" type="submit" disabled={saving}>
                {submitLabel}
              </Button>
            </Right>
          </>
        );
      }}
    </AccessKeyForm>
  );
}
